// hooks/useLegalCompliance.ts
// Manages legal document acceptance state.
// Fetches pending documents on mount and exposes state + accept function.
// The LayoutContainer uses this to show a non-dismissible modal before the tutorial.

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchData, createData } from "api/apiHelpers";
import type { RootState } from "types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LegalDocument {
  id: string;
  document_type: string;
  version: string;
  language: string;
  title: string;
  content: string;
  effective_date: string;
  requires_acceptance: boolean;
}

export interface LegalComplianceContextValue {
  /** Documents the user still needs to accept */
  pendingDocuments: LegalDocument[];
  /** Whether we're still loading pending docs */
  loading: boolean;
  /** Whether there are pending legal docs that block usage */
  hasPendingLegal: boolean;
  /** Accept a specific document */
  acceptDocument: (documentId: string) => Promise<boolean>;
  /** Whether an acceptance is in flight */
  accepting: boolean;
  /** Re-fetch pending documents (e.g. after an accept) */
  refresh: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const LegalComplianceContext =
  createContext<LegalComplianceContextValue | null>(null);

export function useLegalCompliance(): LegalComplianceContextValue {
  const ctx = useContext(LegalComplianceContext);
  if (!ctx) {
    // Safe default when used outside the provider
    return {
      pendingDocuments: [],
      loading: false,
      hasPendingLegal: false,
      acceptDocument: async () => false,
      accepting: false,
      refresh: () => {},
    };
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// State hook (used by the provider in LayoutContainer)
// ---------------------------------------------------------------------------

export function useLegalComplianceState(): LegalComplianceContextValue {
  const [pendingDocuments, setPendingDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const token = useSelector((state: RootState) => state.auth.token);
  const isRehydrated = useSelector(
    (state: RootState) => state._persist?.rehydrated ?? false
  );

  const fetchPending = useCallback(async () => {
    if (!token) {
      setPendingDocuments([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const docs = await fetchData<LegalDocument[]>(
        `/billing/legal/pending/?language=en`
      );
      setPendingDocuments(docs);
    } catch (err) {
      // If the endpoint isn't available yet (e.g. migration not run),
      // don't block the user.
      console.warn("useLegalComplianceState: failed to fetch pending legal documents", err);
      setPendingDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Wait for Redux Persist rehydration before fetching.
  // Without this guard, the hook fires with the initial (empty) auth state
  // before the persisted token is restored — causing a spurious "no token"
  // path that can flash the T&C modal on refresh.
  useEffect(() => {
    if (!isRehydrated) return;
    fetchPending();
  }, [isRehydrated, fetchPending]);

  const acceptDocument = useCallback(
    async (documentId: string): Promise<boolean> => {
      try {
        setAccepting(true);
        await createData("/billing/legal/accept/", {
          document_id: documentId,
        });
        // Remove accepted doc from pending list
        setPendingDocuments((prev) =>
          prev.filter((d) => d.id !== documentId)
        );
        return true;
      } catch (err) {
        console.warn("useLegalComplianceState: failed to accept document", documentId, err);
        return false;
      } finally {
        setAccepting(false);
      }
    },
    []
  );

  // Belt-and-suspenders: while Redux is still rehydrating, keep the hook in
  // a "loading" state so downstream components never see a premature
  // hasPendingLegal = false.
  if (!isRehydrated) {
    return {
      pendingDocuments: [],
      loading: true,
      hasPendingLegal: false,
      acceptDocument,
      accepting: false,
      refresh: fetchPending,
    };
  }

  return {
    pendingDocuments,
    loading,
    hasPendingLegal: pendingDocuments.length > 0,
    acceptDocument,
    accepting,
    refresh: fetchPending,
  };
}

export default useLegalCompliance;
